// src/utils/embedAuth.js
//
// Embed mode: this app is running inside an iframe hosted by Tango's portal
// (see the Grove repo tango_vault_integration docs). Auth arrives via
// postMessage from the parent frame instead of Easy Auth/MSAL — bypassing
// both is the whole point of the token bridge (both are redirect-based and
// break inside an iframe).

const EXPECTED_PARENT_ORIGIN = process.env.REACT_APP_EMBED_PARENT_ORIGIN || null;

// Latched once at page load. Client-side navigation (React Router) rewrites
// window.location.search, so reading it live would silently flip the app out
// of embed mode on the first route change — mid-session, with the NavBar
// reappearing and authAxios dropping the bridge token.
const EMBED_MODE =
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('embed') === '1';

let embedToken = null;
// False until the parent has sent at least one vault:auth/vault:logout.
// Lets subscribers distinguish "parent hasn't spoken yet" (keep waiting)
// from "parent says logged out" (give up).
let parentResponded = false;
let listeners = [];
let navListeners = [];

export const isEmbedMode = () => EMBED_MODE;

// Subscribe to `vault:navigate` messages from the parent. The parent keeps a
// single persistent iframe and drives sub-page changes by postMessage (instead
// of reloading the iframe, which would re-run the whole auth handshake). A
// component inside the Router wires this to react-router's navigate.
export const onEmbedNavigate = (cb) => {
  navListeners.push(cb);
  return () => {
    navListeners = navListeners.filter((l) => l !== cb);
  };
};

export const getEmbedToken = () => embedToken;

let authedNotified = false;

// Embed mode only: tell the parent frame (Tango) that Vault has finished
// authenticating and is ready to be shown, so Tango can swap its own loader
// for the live iframe. Fires at most once; always targeted at the pinned
// parent origin — never '*'. No-op standalone or without a pinned origin.
export const notifyParentAuthed = () => {
  if (!EMBED_MODE || !EXPECTED_PARENT_ORIGIN || authedNotified) return;
  if (typeof window === 'undefined') return;
  authedNotified = true;
  window.parent.postMessage({ type: 'vault:authed' }, EXPECTED_PARENT_ORIGIN);
};

// Subscribe to token changes. If the parent has already sent a message,
// calls back immediately with the current token; otherwise the first call
// happens when the parent first posts. Returns an unsubscribe fn.
export const onEmbedToken = (cb) => {
  listeners.push(cb);
  if (parentResponded) cb(embedToken);
  return () => {
    listeners = listeners.filter((l) => l !== cb);
  };
};

if (EMBED_MODE && typeof window !== 'undefined') {
  if (!EXPECTED_PARENT_ORIGIN) {
    // Fail closed. Without a pinned parent origin we cannot safely accept a
    // token over postMessage — any frame or opener could inject one and pin the
    // user into an attacker-supplied session. So we do NOT attach the listener
    // and do NOT announce readiness; AuthContext's bail timer then routes the
    // user to the unauthorized page. REACT_APP_EMBED_PARENT_ORIGIN is mandatory
    // in embed builds.
    console.error(
      '[embedAuth] REACT_APP_EMBED_PARENT_ORIGIN is not set; refusing to enter the embed token bridge.',
    );
  } else {
    // Embed-only styling hook: styles/embed-theme.js scopes its Tango token
    // overrides to :root[data-embed="1"], so standalone Vault is untouched.
    document.documentElement.setAttribute('data-embed', '1');

    window.addEventListener('message', (event) => {
      // Only trust the pinned parent frame. Origin alone isn't enough: a popup
      // or sibling tab at the same origin passes the origin check but isn't the
      // embedding frame, so require event.source === window.parent too.
      if (event.origin !== EXPECTED_PARENT_ORIGIN) return;
      if (event.source !== window.parent) return;

      const data = event.data;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'vault:auth' && typeof data.token === 'string') {
        embedToken = data.token;
        parentResponded = true;
        listeners.forEach((cb) => cb(embedToken));
      } else if (data.type === 'vault:logout') {
        embedToken = null;
        parentResponded = true;
        listeners.forEach((cb) => cb(null));
      } else if (data.type === 'vault:navigate' && typeof data.path === 'string') {
        navListeners.forEach((cb) => cb(data.path));
      }
    });

    // Handshake: tell the parent we're listening. Without this the parent can
    // post the token before our listener attaches and it's lost forever. Always
    // targeted at the pinned origin — never '*'.
    window.parent.postMessage({ type: 'vault:ready' }, EXPECTED_PARENT_ORIGIN);
  }
}
